import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { addMonths, addQuarters, differenceInCalendarDays, format, isFuture, subMonths, subQuarters } from 'date-fns';
import { combineLatest, ReplaySubject, takeUntil } from 'rxjs';
import { IFilter } from '../../types/filter.interface';
import { PeriodTypes } from './types/period-types.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { KorviuDatePickerComponent } from '../korviu-date-picker/korviu-date-picker.component';
import { KorviuRangePickerComponent } from '../korviu-range-picker/korviu-range-picker.component';
import { enUS, es } from 'date-fns/locale';

@Component({
  selector: 'date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  imports: [TranslateModule, NzButtonModule, KorviuDatePickerComponent],
  standalone: true
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  @Input() public periodParameter!: IFilter[];
  @Input() public periodType!: PeriodTypes;
  @Output() public periodParameterChange = new EventEmitter<IFilter[]>();
  @Output() public periodTypeChange = new EventEmitter<PeriodTypes>();
  public periodControl: FormControl;
  public startDateControl: FormControl;
  public endDateControl: FormControl;
  public pickDateFromCalendarMode = false;
  public PeriodTypes = PeriodTypes;
  private _onDestroy$ = new ReplaySubject<void>(1);
  private _translateService = inject(TranslateService);

  constructor() {
    const initialStart = subQuarters(new Date(), 1);
    const initialEnd = new Date();
    this.periodControl = new FormControl([initialStart, initialEnd]);
    this.startDateControl = new FormControl(initialStart);
    this.endDateControl = new FormControl(initialEnd);
  }

  public ngOnInit(): void {
    this.periodControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(([start, end]: [Date, Date]) => {
      this.periodParameter = [
        { name: 'from', value: start.toISOString() },
        { name: 'to', value: end.toISOString() }
      ];
      this.periodParameterChange.emit(this.periodParameter);
      this.startDateControl.patchValue(start, { emitEvent: false });
      this.endDateControl.patchValue(end, { emitEvent: false });
    });
    combineLatest([this.startDateControl.valueChanges, this.endDateControl.valueChanges])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([start, end]) => {
        if (start && end) {
          this.periodControl.patchValue([start, end], { emitEvent: true });
        }
      });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onCalendarClick(): void {
    this.pickDateFromCalendarMode = !this.pickDateFromCalendarMode;
  }

  public onPrevPeriodCLick(): void {
    if (!this.prevButtonAvailable) return;
    const currentStart = this.startDateControl.value as Date;
    const currentEnd = this.endDateControl.value as Date;
    if (this.periodType === PeriodTypes.MONTH) {
      const newStart = subMonths(currentStart, 1);
      const newEnd = subMonths(currentEnd, 1);
      this.setPeriod(newStart, newEnd, this.periodType);
    } else {
      const newStart = subQuarters(currentStart, 1);
      const newEnd = subQuarters(currentEnd, 1);
      this.setPeriod(newStart, newEnd, this.periodType);
    }
  }

  public onNextPeriodCLick(): void {
    if (!this.nextButtonAvailable) return;
    const currentStart = this.startDateControl.value as Date;
    const currentEnd = this.endDateControl.value as Date;
    if (this.periodType === PeriodTypes.MONTH) {
      const newStart = addMonths(currentStart, 1);
      const newEnd = addMonths(currentEnd, 1);
      this.setPeriod(newStart, newEnd, this.periodType);
    } else {
      const newStart = addQuarters(currentStart, 1);
      const newEnd = addQuarters(currentEnd, 1);
      this.setPeriod(newStart, newEnd, this.periodType);
    }
  }

  public onMonthTypePeriodSelect(): void {
    if (this.pickDateFromCalendarMode) {
      this.pickDateFromCalendarMode = false;
    }
    const end = new Date();
    const start = subMonths(end, 1);
    this.setPeriod(start, end, PeriodTypes.MONTH);
  }

  public onQuarterTypePeriodSelect(): void {
    if (this.pickDateFromCalendarMode) {
      this.pickDateFromCalendarMode = false;
    }
    const end = new Date();
    const start = subQuarters(end, 1);
    this.setPeriod(start, end, PeriodTypes.QUARTER);
  }

  public get nextButtonAvailable(): boolean {
    const start = this.startDateControl.value as Date;
    const end = this.endDateControl.value as Date;

    if (this.periodType === PeriodTypes.MONTH) {
      return !isFuture(addMonths(start, 1)) && !isFuture(addMonths(end, 1));
    } else {
      return !isFuture(addQuarters(start, 1)) && !isFuture(addQuarters(end, 1));
    }
  }

  public get prevButtonAvailable(): boolean {
    const start = this.startDateControl.value as Date;
    const end = this.endDateControl.value as Date;

    if (this.periodType === PeriodTypes.MONTH) {
      return format(subMonths(start, 1), 'yyyy') !== '2022' && format(subMonths(end, 1), 'yyyy') !== '2022';
    } else {
      return format(subQuarters(start, 1), 'yyyy') !== '2022' && format(subQuarters(end, 1), 'yyyy') !== '2022';
    }
  }

  public get formattedPeriod(): string {
    const locale = this._translateService.currentLang === 'es' ? es : enUS;
    const startFormat = locale && this.periodType === PeriodTypes.MONTH ? 'MMMM yyyy' : 'LLL yyyy';
    let startStr = format(this.startDateControl.value, startFormat, { locale });
    let endStr = format(this.endDateControl.value, 'LLL yyyy', { locale });
    startStr = startStr.charAt(0).toUpperCase() + startStr.slice(1);
    endStr = endStr.charAt(0).toUpperCase() + endStr.slice(1);
    if (this.periodType === PeriodTypes.MONTH) {
      return startStr;
    } else {
      return `${startStr} - ${endStr}`;
    }
  }

  public disableStartDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;
    const end = this.endDateControl.value as Date;
    if (end) {
      return differenceInCalendarDays(currentDate, end) > 0;
    }
    return false;
  };

  public disableEndDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;

    const start = this.startDateControl.value as Date;
    if (start) {
      return differenceInCalendarDays(currentDate, start) < 0;
    }
    return false;
  };

  private setPeriod(start: Date, end: Date, type: PeriodTypes): void {
    this.periodType = type;
    this.periodTypeChange.emit(type);
    this.periodControl.patchValue([start, end], { emitEvent: true });
  }
}
